import {
  CheckCircle as CheckCircleIcon,
  OpenInNew as OpenInNewIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import DocumentPreview from "~/components/Document/documentPreview";
import FormField from "~/components/Project/formField";
import { CommonButton } from "~/components/ui/commonButton";
import { MessageWithLinks } from "~/components/ui/messageWithLinks";
import Modal from "~/components/ui/modal";
import Stepper, { type StepperStep } from "~/components/ui/stepper";
import { TOAST_MESSAGES } from "~/constants/messages/toastMessages";
import { DocumentApi } from "~/functions/api/document";

import type { KintoneAdminInfo } from "~/functions/api/kintoneAdmin";
import { KintoneSystemApi, type VerifyKintoneAuthReason } from "~/functions/api/kintoneSystem";
import type { Document } from "~/interface/document";
import { getDocumentTypeName } from "~/interface/document";
import type { RootState } from "~/store";
import {
  type ConsistencyValidationResult,
  validateDocumentConsistency,
} from "~/utils/documentConsistencyValidation";
import type { AppInfo, FieldInfo } from "../AppConfigTable";
import ConnectionStatus from "../ConnectionStatus";
import CustomizeSelectionDialog, {
  type AppCustomizeSelection,
  CUSTOMIZE_LIMIT_PER_APP,
  type CustomizeItem,
} from "../CustomizeSelectionDialog";
import styles from "./index.module.scss";

// コンテンツがマークダウン形式かどうかを判定
const isMarkdownContent = (content: string): boolean => {
  try {
    const parsed = JSON.parse(content);
    // オブジェクトまたは配列の場合はJSON形式
    if (typeof parsed === "object" && parsed !== null) {
      return false;
    }
    // 文字列の場合（JSONエスケープされたMarkdown）はマークダウン
    return typeof parsed === "string";
  } catch {
    // JSONパースに失敗した場合はマークダウンとして扱う
    return true;
  }
};

interface DocumentInfo {
  id: string;
  content: string;
  isMarkdown: boolean;
}

type ConnectionStatusType = "idle" | "connecting" | "connected" | "error";

export interface KintoneCredentials {
  subdomain: string;
  userId: string;
  password: string;
}

export interface CreateAppData {
  credentials: KintoneCredentials;
  spaceName: string;
  apps: AppInfo[];
  includeSampleData: boolean;
  /** アプリごとに適用するカスタマイズ機能名リスト（上限超過時のみ設定） */
  selectedCustomizeIds?: Record<string, string[]>;
}

interface CreateAppModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateAppData) => Promise<void>;
  onRegenerate: () => void;
  kintoneAdminInfo: KintoneAdminInfo | null;
}

const steps: StepperStep[] = [
  { id: 1, label: "kintone情報" },
  { id: 2, label: "確認" },
];

const CreateAppModal: React.FC<CreateAppModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  onRegenerate,
  kintoneAdminInfo,
}) => {
  const { currentProject, currentUser } = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatusType>("idle");
  const [credentials, setCredentials] = useState<KintoneCredentials>({
    subdomain: "",
    userId: "",
    password: "",
  });
  const [spaceName, setSpaceName] = useState(currentProject?.name ?? "");
  const [apps, setApps] = useState<AppInfo[]>([]);
  const [includeSampleData, setIncludeSampleData] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isProcessing, setIsProcessing] = useState(false);
  const connectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [documents, setDocuments] = useState<{
    appDesign: DocumentInfo | null;
    fieldDesign: DocumentInfo | null;
    customizeSpec: DocumentInfo | null;
  }>({
    appDesign: null,
    fieldDesign: null,
    customizeSpec: null,
  });

  // 整合性チェック用のDocument全体を保持
  const [fullDocuments, setFullDocuments] = useState<{
    appDesign: Document | null;
    fieldDesign: Document | null;
    customizeSpec: Document | null;
  }>({
    appDesign: null,
    fieldDesign: null,
    customizeSpec: null,
  });

  // 整合性チェック関連のstate
  const [validationResult, setValidationResult] = useState<ConsistencyValidationResult | null>(
    null,
  );

  // カスタマイズ選択ダイアログ関連のstate
  const [isCustomizeSelectionOpen, setIsCustomizeSelectionOpen] = useState(false);
  // 上限超過アプリのカスタマイズ情報
  const [appsExceedingLimit, setAppsExceedingLimit] = useState<
    { appName: string; items: CustomizeItem[] }[]
  >([]);
  // 確定済み選択結果（appName -> 機能名リスト）
  const [confirmedCustomizeIds, setConfirmedCustomizeIds] = useState<
    Record<string, string[]> | undefined
  >(undefined);
  // 送信待ちのCreateAppData（カスタマイズ選択後に使用）
  const pendingSubmitDataRef = useRef<CreateAppData | null>(null);

  // ドキュメント情報を取得
  useEffect(() => {
    const fetchDocuments = async () => {
      if (!currentProject?.id || !currentUser?.organization_id) return;

      const appDesignId = currentProject.outputs?.["app-design"]?.id;
      const fieldDesignId = currentProject.outputs?.["field-design"]?.id;
      const customizeSpecId = currentProject.outputs?.["customize-specification"]?.id;

      const documentApi = new DocumentApi();
      const docs: typeof documents = {
        appDesign: null,
        fieldDesign: null,
        customizeSpec: null,
      };
      const fullDocs: typeof fullDocuments = {
        appDesign: null,
        fieldDesign: null,
        customizeSpec: null,
      };

      // アプリ設計書を取得
      if (appDesignId) {
        try {
          const appDesignDoc = await documentApi.get(
            currentUser.organization_id,
            currentProject.id,
            appDesignId,
          );
          if (appDesignDoc) {
            fullDocs.appDesign = appDesignDoc;
            if (appDesignDoc.content) {
              docs.appDesign = {
                id: appDesignId,
                content: appDesignDoc.content,
                isMarkdown: isMarkdownContent(appDesignDoc.content),
              };
            }
          }
        } catch (e) {
          console.error("Failed to fetch app design:", e);
        }
      }

      // フィールド設計書を取得
      if (fieldDesignId) {
        try {
          const fieldDesignDoc = await documentApi.get(
            currentUser.organization_id,
            currentProject.id,
            fieldDesignId,
          );
          if (fieldDesignDoc) {
            fullDocs.fieldDesign = fieldDesignDoc;
            if (fieldDesignDoc.content) {
              docs.fieldDesign = {
                id: fieldDesignId,
                content: fieldDesignDoc.content,
                isMarkdown: isMarkdownContent(fieldDesignDoc.content),
              };

              // アプリ情報を抽出
              const parsed = JSON.parse(fieldDesignDoc.content);
              const appsArray = Array.isArray(parsed) ? parsed : parsed.apps || [];

              // カスタマイズ仕様書からカスタマイズ対象アプリを取得
              let customizeApps: string[] = [];
              if (customizeSpecId) {
                try {
                  const customizeDoc = await documentApi.get(
                    currentUser.organization_id,
                    currentProject.id,
                    customizeSpecId,
                  );
                  if (customizeDoc) {
                    fullDocs.customizeSpec = customizeDoc;
                    if (customizeDoc.content) {
                      docs.customizeSpec = {
                        id: customizeSpecId,
                        content: customizeDoc.content,
                        isMarkdown: isMarkdownContent(customizeDoc.content),
                      };

                      const customizeParsed = JSON.parse(customizeDoc.content);
                      if (customizeParsed.rows) {
                        customizeApps = customizeParsed.rows
                          .map((row: string[]) => row[1])
                          .filter(Boolean);
                      }
                    }
                  }
                } catch (e) {
                  console.error("Failed to fetch customize spec:", e);
                }
              }

              // アプリ情報を変換
              const appInfos: AppInfo[] = (
                appsArray as Array<{
                  app_id: string;
                  app_name: string;
                  properties?: Array<{
                    フィールド名: string;
                    フィールドコード: string;
                    フィールドタイプ: string;
                  }>;
                }>
              ).map((app) => ({
                app_id: app.app_id,
                app_name: app.app_name,
                fields: (app.properties || []).map(
                  (prop): FieldInfo => ({
                    name: prop.フィールド名,
                    code: prop.フィールドコード,
                    type: prop.フィールドタイプ,
                  }),
                ),
                hasCustomize: customizeApps.includes(app.app_name),
              }));

              setApps(appInfos);
            }
          }
        } catch (e) {
          console.error("Failed to fetch field design:", e);
        }
      }

      setDocuments(docs);
      setFullDocuments(fullDocs);

      // 整合性チェックを実行
      const result = validateDocumentConsistency(
        fullDocs.appDesign,
        fullDocs.fieldDesign,
        fullDocs.customizeSpec,
      );
      setValidationResult(result);
    };

    if (isOpen) {
      fetchDocuments();
    }
  }, [isOpen, currentProject, currentUser]);

  // kintoneAdmin登録済みの場合、サブドメインをプリフィル
  // biome-ignore lint/correctness/useExhaustiveDependencies: credentials をdependencyに入れると無限ループになる
  useEffect(() => {
    if (
      isOpen &&
      kintoneAdminInfo?.is_registered &&
      kintoneAdminInfo.subdomain &&
      !credentials.subdomain
    ) {
      setCredentials((prev) => ({ ...prev, subdomain: kintoneAdminInfo.subdomain ?? "" }));
    }
  }, [isOpen, kintoneAdminInfo]);

  const handleClose = useCallback(() => {
    if (connectTimerRef.current) {
      clearTimeout(connectTimerRef.current);
      connectTimerRef.current = null;
    }
    setCurrentStep(1);
    setConnectionStatus("idle");
    setCredentials({ subdomain: "", userId: "", password: "" });
    setSpaceName(currentProject?.name ?? "");
    setErrorMessage("");
    setIsProcessing(false);
    setIncludeSampleData(true);
    setDocuments({ appDesign: null, fieldDesign: null, customizeSpec: null });
    setFullDocuments({ appDesign: null, fieldDesign: null, customizeSpec: null });
    setValidationResult(null);
    setApps([]);
    setIsCustomizeSelectionOpen(false);
    setAppsExceedingLimit([]);
    setConfirmedCustomizeIds(undefined);
    pendingSubmitDataRef.current = null;
    onClose();
  }, [onClose, currentProject?.name]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCredentials((prev) => ({ ...prev, [name]: value }));
    setErrorMessage("");
  };

  const isFormValid = credentials.subdomain && credentials.userId && credentials.password;

  // エラー理由に応じたメッセージを取得
  const getErrorMessageByReason = (reason: VerifyKintoneAuthReason, message?: string): string => {
    const messages = TOAST_MESSAGES.APP_DEVELOPMENT;
    switch (reason) {
      case "authentication_failed":
        return messages.AUTH_FAILED;
      case "permission_denied":
        return messages.PERMISSION_DENIED;
      case "subdomain_not_found":
        return messages.SUBDOMAIN_NOT_FOUND;
      case "timeout":
        return messages.CONNECTION_TIMEOUT;
      case "rate_limited":
        return messages.RATE_LIMITED;
      case "feature_disabled":
        return message || messages.FEATURE_DISABLED;
      case "bad_request":
        return message || messages.BAD_REQUEST;
      default:
        return message || messages.UNEXPECTED_ERROR;
    }
  };

  const handleConnect = async () => {
    if (!isFormValid) return;
    if (!currentUser?.organization_id || !currentProject?.id) {
      setConnectionStatus("error");
      setErrorMessage(TOAST_MESSAGES.APP_DEVELOPMENT.PROJECT_INFO_NOT_FOUND);
      return;
    }

    if (connectTimerRef.current) {
      clearTimeout(connectTimerRef.current);
      connectTimerRef.current = null;
    }
    setConnectionStatus("connecting");
    setErrorMessage("");

    try {
      // verifyKintoneAuth で接続テスト（admin登録有無にかかわらず検証のみ）
      const kintoneSystemApi = new KintoneSystemApi();
      const result = await kintoneSystemApi.verifyKintoneAuth(
        currentUser.organization_id,
        currentProject.id,
        {
          subdomain: credentials.subdomain,
          login_id: credentials.userId,
          password: credentials.password,
        },
      );

      if (result.ok) {
        setConnectionStatus("connected");

        // 接続成功後、少し待ってから確認画面へ
        if (connectTimerRef.current) clearTimeout(connectTimerRef.current);
        connectTimerRef.current = setTimeout(() => {
          connectTimerRef.current = null;
          setCurrentStep(2);
        }, 1000);
      } else {
        setConnectionStatus("error");
        setErrorMessage(getErrorMessageByReason(result.reason, result.message));
      }
    } catch {
      setConnectionStatus("error");
      setErrorMessage(TOAST_MESSAGES.APP_DEVELOPMENT.NETWORK_ERROR);
    }
  };

  const handleBack = () => {
    if (connectTimerRef.current) {
      clearTimeout(connectTimerRef.current);
      connectTimerRef.current = null;
    }
    if (currentStep === 2) {
      setCurrentStep(1);
      setConnectionStatus("idle");
    } else {
      handleClose();
    }
  };

  /**
   * カスタマイズ仕様書の内容から、アプリごとにグループ化されたカスタマイズアイテムを返す。
   * 10件超のアプリのみ。
   */
  const buildAppsExceedingLimit = (
    customizeContent: string,
  ): { appName: string; items: CustomizeItem[] }[] => {
    try {
      const parsed = JSON.parse(customizeContent);
      const rows: string[][] = parsed.rows || [];

      // アプリ名でグループ化
      const grouped: Record<string, CustomizeItem[]> = {};
      for (const row of rows) {
        const functionName = row[0] ?? "";
        const appName = row[1] ?? "";
        if (!appName || !functionName) continue;
        if (!grouped[appName]) grouped[appName] = [];
        grouped[appName].push({ functionName, appName });
      }

      // 上限超過のアプリのみ返す
      return Object.entries(grouped)
        .filter(([, items]) => items.length > CUSTOMIZE_LIMIT_PER_APP)
        .map(([appName, items]) => ({ appName, items }));
    } catch {
      return [];
    }
  };

  /**
   * 実際にonSubmitを呼ぶ処理。selectedCustomizeIds が渡された場合はそれを使用。
   */
  const executeSubmit = async (selectedCustomizeIds?: Record<string, string[]>) => {
    setIsProcessing(true);
    const submitData: CreateAppData = {
      credentials,
      spaceName,
      apps,
      includeSampleData,
    };
    if (selectedCustomizeIds !== undefined) {
      submitData.selectedCustomizeIds = selectedCustomizeIds;
    }
    try {
      await onSubmit(submitData);
      handleClose();
    } catch (error) {
      console.error("Failed to create app:", error);
      toast.error(error instanceof Error ? error.message : String(error));
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * カスタマイズ選択ダイアログで「この選択で実行」が押されたとき
   */
  const handleCustomizeSelectionConfirm = async (selections: AppCustomizeSelection[]) => {
    setIsCustomizeSelectionOpen(false);

    // 選択結果をRecord<appName, functionNames[]>に変換
    const selectedIds: Record<string, string[]> = {};
    for (const s of selections) {
      selectedIds[s.appName] = s.selectedFunctionNames;
    }
    setConfirmedCustomizeIds(selectedIds);

    await executeSubmit(selectedIds);
  };

  const handleCustomizeSelectionCancel = () => {
    setIsCustomizeSelectionOpen(false);
    pendingSubmitDataRef.current = null;
  };

  const handleNext = async () => {
    if (currentStep === 1) {
      if (connectionStatus === "connected") {
        setCurrentStep(2);
      } else {
        await handleConnect();
      }
    } else if (currentStep === 2) {
      // カスタマイズ仕様書があれば上限チェック
      if (documents.customizeSpec?.content) {
        const exceeding = buildAppsExceedingLimit(documents.customizeSpec.content);
        if (exceeding.length > 0) {
          // 選択ダイアログを表示
          setAppsExceedingLimit(exceeding);
          setIsCustomizeSelectionOpen(true);
          return;
        }
      }
      // 上限超過なし → そのまま実行
      await executeSubmit(confirmedCustomizeIds);
    }
  };

  const isNextDisabled = () => {
    if (currentStep === 1) {
      return !isFormValid || connectionStatus === "connecting" || requiresKintoneAdmin;
    }
    if (currentStep === 2) {
      return !spaceName || isProcessing || requiresKintoneAdmin;
    }
    return false;
  };

  // 不整合があるかどうか
  const hasInconsistency = validationResult && !validationResult.isConsistent;

  // カスタマイズ仕様書が現世代（app-designと整合）かどうか
  const isCustomizeSpecCurrentGeneration =
    documents.customizeSpec !== null &&
    validationResult !== null &&
    !validationResult.mismatches.some(
      (m) => m.documentType === "customize-specification" && m.referenceType === "app-design",
    );

  // カスタマイズ仕様書が現世代で存在するのに管理者未登録 → ブロック
  const requiresKintoneAdmin = isCustomizeSpecCurrentGeneration && !kintoneAdminInfo?.is_registered;

  // ドキュメントを新しいタブで開く
  const handleDocumentClick = (documentType: string, documentId: string | undefined) => {
    if (!documentId || !currentProject?.id || !currentUser?.organization_id) return;
    const url = `/${currentUser.organization_id}/project/${currentProject.id}/${documentType}/${documentId}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const renderKintoneInfoStep = () => {
    if (connectionStatus === "connecting" || connectionStatus === "connected") {
      return <ConnectionStatus status={connectionStatus} errorMessage={errorMessage} />;
    }

    return (
      <div className={styles["stepContent"]}>
        {/* 情報セクション: 管理者ステータス + 権限 + 説明 */}
        <div className={styles["infoSection"]}>
          {/* 管理者登録ステータスバナー */}
          {kintoneAdminInfo?.is_registered ? (
            <div className={styles["adminStatusRegistered"]}>
              <CheckCircleIcon sx={{ width: 18, height: 18, flexShrink: 0 }} />
              <div className={styles["adminStatusContent"]}>
                <span className={styles["adminStatusLabel"]}>kintoneシステム管理者: 登録済み</span>
                <div className={styles["adminStatusDetails"]}>
                  <span>
                    <span className={styles["adminStatusDetailLabel"]}>サブドメイン:</span>{" "}
                    {kintoneAdminInfo.subdomain}
                  </span>
                  <span className={styles["adminStatusDivider"]}>|</span>
                  <span>
                    <span className={styles["adminStatusDetailLabel"]}>ログイン名（kintone）:</span>{" "}
                    {kintoneAdminInfo.login_id}
                  </span>
                </div>
              </div>
            </div>
          ) : requiresKintoneAdmin ? (
            <div className={styles["adminStatusNotRegistered"]}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <WarningIcon sx={{ width: 18, height: 18 }} />
                <span>kintoneシステム管理者の登録が必要です</span>
              </div>
              <p className={styles["adminRequiredDescription"]}>
                カスタマイズ仕様書が含まれるため、先にkintoneシステム管理者の登録が必要です。
              </p>
              <button
                type="button"
                className={styles["adminSettingsLink"]}
                onClick={() => {
                  if (currentUser?.organization_id && currentProject?.id) {
                    onClose();
                    navigate(
                      `/${currentUser.organization_id}/project/${currentProject.id}/settings`,
                    );
                  }
                }}
              >
                設定画面で登録する →
              </button>
            </div>
          ) : null}
        </div>

        {/* 認証情報入力セクション */}
        <div className={styles["formCard"]}>
          <div className={styles["formCardHeader"]}>
            <h4 className={styles["formCardTitle"]}>認証情報</h4>
            <p className={styles["formCardDescription"]}>
              ここで入力する認証情報はアプリ生成時のみ使用され、保存されません。
              {kintoneAdminInfo?.is_registered &&
                "プロジェクト設定で登録済みのシステム管理者情報はカスタマイズ適用に使用されます。"}
            </p>
          </div>

          {/* 必要権限 */}
          <div className={styles["permissionSection"]}>
            <span className={styles["permissionLabel"]}>必要な権限</span>
            <ul className={styles["permissionList"]}>
              <li>スペースの作成</li>
              <li>アプリの作成</li>
              <li>アプリの管理</li>
            </ul>
          </div>

          <div className={styles["formFields"]}>
            <FormField
              label="サブドメイン"
              name="subdomain"
              type="text"
              placeholder="サブドメインを入力"
              value={credentials.subdomain}
              onChange={handleInputChange}
              required
              readOnly={!!kintoneAdminInfo?.is_registered && !!credentials.subdomain}
              suffix=".cybozu.com"
            />
            <FormField
              label="ログイン名（kintone）"
              name="userId"
              type="text"
              placeholder="ログイン名を入力"
              value={credentials.userId}
              onChange={handleInputChange}
              required
            />
            <FormField
              label="パスワード（kintone）"
              name="password"
              type="password"
              placeholder="パスワードを入力"
              value={credentials.password}
              onChange={handleInputChange}
              required
            />
          </div>

          {connectionStatus === "error" && errorMessage && (
            <p className={styles["errorMessage"]}>
              <MessageWithLinks text={errorMessage} />
            </p>
          )}
        </div>
      </div>
    );
  };

  const renderConfirmationStep = () => (
    <div className={styles["confirmationContent"]}>
      {/* 整合性警告セクション（インライン表示） */}
      {hasInconsistency && validationResult && (
        <div className={styles["consistencyWarning"]}>
          <div className={styles["warningHeader"]}>
            <div className={styles["warningIconWrapper"]}>
              <div className={styles["warningPulse"]} />
              <WarningIcon className={styles["warningIcon"] ?? ""} />
            </div>
            <div className={styles["warningContent"]}>
              <h3 className={styles["warningTitle"]}>ドキュメントの世代に不整合があります</h3>
              <p className={styles["warningDescription"]}>
                このまま続行すると意図しない結果になる可能性があります。
              </p>
            </div>
          </div>

          <div className={styles["mismatchList"]}>
            {validationResult.mismatches.map((mismatch, index) => (
              <div
                key={`${mismatch.documentType}-${mismatch.referenceType}`}
                className={styles["mismatchItem"]}
                style={{ animationDelay: `${index * 0.08}s` }}
              >
                <div className={styles["mismatchFlow"]}>
                  <span className={styles["mismatchNode"]}>
                    {getDocumentTypeName(mismatch.documentType)}
                    {mismatch.documentModifiedAt && (
                      <span className={styles["mismatchDate"]}>{mismatch.documentModifiedAt}</span>
                    )}
                  </span>
                  <span className={styles["mismatchArrow"]}>→</span>
                  <span className={styles["mismatchNode"]}>
                    {getDocumentTypeName(mismatch.referenceType)}
                    {mismatch.referenceModifiedAt && (
                      <span className={styles["mismatchDate"]}>{mismatch.referenceModifiedAt}</span>
                    )}
                  </span>
                </div>
                <p className={styles["mismatchMessage"]}>{mismatch.message}</p>
              </div>
            ))}
          </div>

          <div className={styles["warningActions"]}>
            <CommonButton onClick={onRegenerate} className={styles["regenerateButton"] ?? ""}>
              ドキュメントを再生成する
            </CommonButton>
            <span className={styles["orContinue"]}>または、このまま生成スタートで続行できます</span>
          </div>
        </div>
      )}

      {requiresKintoneAdmin && (
        <div className={styles["adminRequiredWarning"]}>
          <WarningIcon sx={{ width: 18, height: 18, flexShrink: 0 }} />
          <div className={styles["adminRequiredContent"]}>
            <span className={styles["adminRequiredLabel"]}>
              kintoneシステム管理者の登録が必要です
            </span>
            <p className={styles["adminRequiredDescription"]}>
              カスタマイズ仕様書が含まれるため、生成スタートにはkintoneシステム管理者の登録が必要です。
            </p>
            <button
              type="button"
              className={styles["adminSettingsLink"]}
              onClick={() => {
                if (currentUser?.organization_id && currentProject?.id) {
                  onClose();
                  navigate(`/${currentUser.organization_id}/project/${currentProject.id}/settings`);
                }
              }}
            >
              設定画面で登録する →
            </button>
          </div>
        </div>
      )}

      <div className={styles["spaceNameSection"]}>
        <label htmlFor="spaceName" className={styles["label"]}>
          kintoneスペース名
          <span className={styles["required"]}>*</span>
        </label>
        <input
          id="spaceName"
          type="text"
          className={styles["input"]}
          value={spaceName}
          onChange={(e) => setSpaceName(e.target.value)}
          placeholder="例: システム開発"
        />
      </div>

      <div className={styles["sampleDataCheckbox"]}>
        <label className={styles["checkboxLabel"]}>
          <input
            type="checkbox"
            checked={includeSampleData}
            onChange={(e) => setIncludeSampleData(e.target.checked)}
          />
          <span>作成するアプリにサンプルデータを含める</span>
        </label>
      </div>

      {/* ドキュメントプレビュー（マークダウン・JSON両対応） */}
      <div className={styles["documentsSection"]}>
        <h3 className={styles["sectionTitle"]}>ドキュメント</h3>
        <div className={styles["documentPreviews"]}>
          {documents.appDesign?.content && (
            <button
              type="button"
              className={styles["documentPreviewCard"]}
              onClick={() => handleDocumentClick("app-design", documents.appDesign?.id)}
            >
              <h4 className={styles["documentTitle"]}>
                <span>アプリ設計書</span>
                <OpenInNewIcon className={styles["openInNewIcon"] ?? ""} />
              </h4>
              <div className={styles["documentPreviewContent"]}>
                <DocumentPreview
                  content={documents.appDesign.content}
                  documentType="app-design"
                  previewMode="css"
                  height={150}
                />
              </div>
            </button>
          )}
          {documents.fieldDesign?.content && (
            <button
              type="button"
              className={styles["documentPreviewCard"]}
              onClick={() => handleDocumentClick("field-design", documents.fieldDesign?.id)}
            >
              <h4 className={styles["documentTitle"]}>
                <span>フィールド設計書</span>
                <OpenInNewIcon className={styles["openInNewIcon"] ?? ""} />
              </h4>
              <div className={styles["documentPreviewContent"]}>
                <DocumentPreview
                  content={documents.fieldDesign.content}
                  documentType="field-design"
                  previewMode="css"
                  height={150}
                />
              </div>
            </button>
          )}
          {documents.customizeSpec?.content && (
            <button
              type="button"
              className={styles["documentPreviewCard"]}
              onClick={() =>
                handleDocumentClick("customize-specification", documents.customizeSpec?.id)
              }
            >
              <h4 className={styles["documentTitle"]}>
                <span>カスタマイズ仕様書</span>
                <OpenInNewIcon className={styles["openInNewIcon"] ?? ""} />
              </h4>
              <div className={styles["documentPreviewContent"]}>
                <DocumentPreview
                  content={documents.customizeSpec.content}
                  documentType="customize-specification"
                  previewMode="css"
                  height={150}
                />
              </div>
            </button>
          )}
        </div>
      </div>
    </div>
  );

  const getNextButtonLabel = () => {
    if (currentStep === 2) return isProcessing ? "作成中..." : "生成スタート";
    if (connectionStatus === "connecting") return "接続中...";
    return "次へ";
  };

  const modalContent = (
    <div className={styles["modalBody"]}>
      <div className={styles["stepperContainer"]}>
        <Stepper steps={steps} currentStep={currentStep} />
      </div>

      <div className={styles["contentContainer"]}>
        {currentStep === 1 ? renderKintoneInfoStep() : renderConfirmationStep()}
      </div>
    </div>
  );

  // 接続中・接続成功時はフッターを非表示（自動でStep2に遷移するため）
  const shouldShowFooter =
    connectionStatus === "idle" || connectionStatus === "error" || currentStep === 2;

  const modalFooter = shouldShowFooter ? (
    <div className={styles["modalFooter"]}>
      <CommonButton variant="label" onClick={handleBack} disabled={isProcessing}>
        戻る
      </CommonButton>
      <CommonButton onClick={handleNext} disabled={isNextDisabled()}>
        {getNextButtonLabel()}
      </CommonButton>
    </div>
  ) : undefined;

  return (
    <>
      <Modal
        label="アプリ生成"
        isOpen={isOpen}
        onClose={handleClose}
        content={modalContent}
        footer={modalFooter}
        className="createAppModal"
        usePortal
      />
      <CustomizeSelectionDialog
        open={isCustomizeSelectionOpen}
        appsExceedingLimit={appsExceedingLimit}
        onConfirm={handleCustomizeSelectionConfirm}
        onCancel={handleCustomizeSelectionCancel}
      />
    </>
  );
};

export default CreateAppModal;

export type { AppInfo, FieldInfo };
